import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

/**
 * Dev-mode unlock — Android-style "tap 7 times on the logo within 3 seconds"
 * to reveal hidden developer options (Log Viewer toggle in Configurations).
 *
 * State is persisted in localStorage under `dev_mode_enabled`. Once enabled,
 * stays enabled until explicitly disabled from the dev menu.
 */
@Injectable({ providedIn: "root" })
export class DevModeService {
  private static readonly STORAGE_KEY = "dev_mode_enabled";
  private static readonly REQUIRED_TAPS = 7;
  private static readonly WINDOW_MS = 3000;

  private tapCount = 0;
  private firstTapAt = 0;
  private subject: BehaviorSubject<boolean>;
  readonly enabled$: Observable<boolean>;

  constructor() {
    const persisted = this.readPersisted();
    this.subject = new BehaviorSubject<boolean>(persisted);
    this.enabled$ = this.subject.asObservable();
  }

  isEnabled(): boolean {
    return this.subject.value;
  }

  /**
   * Hook the logo tap. Returns:
   *   - unlocked:     true once the threshold is reached (also when already on)
   *   - justUnlocked: true ONLY on the exact tap that crosses the threshold —
   *                   caller uses this to show "Mode développeur activé"
   *                   exactly once, not on every subsequent tap.
   *   - remainingTaps: how many taps are still needed to unlock (0 once unlocked)
   */
  registerLogoTap(): { unlocked: boolean; justUnlocked: boolean; remainingTaps: number } {
    if (this.isEnabled()) {
      return { unlocked: true, justUnlocked: false, remainingTaps: 0 };
    }
    const now = Date.now();
    if (this.tapCount === 0 || now - this.firstTapAt > DevModeService.WINDOW_MS) {
      this.tapCount = 1;
      this.firstTapAt = now;
    } else {
      this.tapCount += 1;
    }
    if (this.tapCount >= DevModeService.REQUIRED_TAPS) {
      this.enable();
      this.tapCount = 0;
      return { unlocked: true, justUnlocked: true, remainingTaps: 0 };
    }
    return {
      unlocked: false,
      justUnlocked: false,
      remainingTaps: DevModeService.REQUIRED_TAPS - this.tapCount,
    };
  }

  enable(): void {
    try {
      localStorage.setItem(DevModeService.STORAGE_KEY, "1");
    } catch (_) { }
    this.subject.next(true);
  }

  disable(): void {
    try {
      localStorage.removeItem(DevModeService.STORAGE_KEY);
    } catch (_) { }
    // Reset the rolling-window state, otherwise a stale firstTapAt could
    // make the very next tap count as #2 toward re-unlock.
    this.tapCount = 0;
    this.firstTapAt = 0;
    this.subject.next(false);
  }

  private readPersisted(): boolean {
    try {
      return localStorage.getItem(DevModeService.STORAGE_KEY) === "1";
    } catch (_) {
      return false;
    }
  }
}
